/**
 * Minimal waterfall chart data helpers for the analyst result cards.
 *
 * These are intentionally isolated from analysisUtils.js to avoid triggering
 * the circular dependency between analysisUtils ↔ analysisStore that causes
 * a ReferenceError when those modules are imported in new entry points.
 */

/**
 * Convert a flat array of {name, value, positiveBgColor?, negativeBgColor?}
 * into WaterfallChart-ready data with start/end/backgroundColor on each item.
 * The last item becomes the running total bar.
 */
function parseWaterfallData(data) {
  let total = 0;
  data.forEach((item, index, arr) => {
    total += item.value;

    const name = typeof item.name === 'string' ? item.name.split(' ') : item.name;
    item.name = name;

    if (index === 0) {
      item.start = 0;
      item.end = item.value;
    } else if (index < arr.length - 1) {
      item.start = arr[index - 1].end;
      item.end = item.start + item.value;
    } else {
      // Last item = total bar.
      item.start = 0;
      item.end = total;
      item.value = total;
    }

    item.backgroundColor =
      item.value < 0
        ? (item.negativeBgColor ?? '#c3d9fc')
        : (item.positiveBgColor ?? '#c3d9fc');
  });
  return data;
}

/**
 * Adapts TEA breakdown [{label, cost}] → WaterfallChart data (orange palette).
 */
export function parseTEAForWaterfall(breakdown) {
  const items = breakdown.map(({ label, cost }) => ({
    name: label,
    value: cost,
    negativeBgColor: '#B2F0DC',
    positiveBgColor: '#FFBC71',
  }));
  items.push({ name: 'Total', value: 0, positiveBgColor: '#E8750F' });
  return parseWaterfallData(items);
}

/**
 * Adapts LCA breakdown [{label, value}] → WaterfallChart data (blue palette).
 */
export function parseLCAForWaterfall(breakdown) {
  const items = breakdown.map(({ label, value }) => ({
    name: label,
    value,
    negativeBgColor: '#86efac',  // green-300 — negative = credit/offset
    positiveBgColor: '#93c5fd',  // blue-300
  }));
  items.push({ name: 'Total', value: 0, positiveBgColor: '#2563eb' });
  return parseWaterfallData(items);
}
